import {MarkGithubIcon} from '@primer/octicons-react'
import {Button, Grid, Heading, Text} from '@primer/react-brand/esm'
import {clsx} from 'clsx'
import {useId} from 'react'

import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'

import Styles from './FeatureGrid.module.css'

import type {Cta, ThemedVisual} from '../../../Index.types'
import {ColorDivider} from '../ColorDivider/ColorDivider'
import {FeatureGridVisual} from './FeatureGridVisual/FeatureGridVisual'

interface FeatureGridItem {
  title: string
  description: string
  icon?: 'mark-github'
  visual: ThemedVisual
  cta1: Cta
  cta2?: Cta
}

export interface FeatureGridProps {
  title: string
  hasColorDivider?: boolean
  layout: 'two-column' | 'two-then-three'
  items: FeatureGridItem[]
}

export type FeatureGridPropsData = Omit<FeatureGridProps, 'hasColorDivider' | 'layout'>

export const defaultProps: Partial<FeatureGridProps> = {}

const SHADOW_COLORS = ['green', 'blueLight', 'pink', 'orange', 'blueDark'] as const

export const FeatureGrid = (props: FeatureGridProps) => {
  const initializedProps: FeatureGridProps = {...defaultProps, ...props}
  const {title, items, hasColorDivider, layout} = initializedProps
  const titleId = useId()
  const isPlatform = layout === 'two-column'

  return (
    <section
      className={clsx(Styles.FeatureGrid, isPlatform && Styles['FeatureGrid--platform'])}
      aria-labelledby={titleId}
    >
      <div className={Styles.FeatureGrid__header}>
        <Heading as="h2" size="3" weight="normal" id={titleId}>
          {title}
        </Heading>
      </div>
      <div className={Styles.FeatureGrid__gridFrame}>
        <div className={Styles.FeatureGrid__gridContent}>
          <Grid columnGap="none" rowGap="none" enableGutters={false}>
            {items.map((item, index) => (
              <Grid.Column
                key={item.title}
                as="section"
                aria-labelledby={`${titleId}-${index}`}
                span={{xsmall: 12, large: isPlatform || index < 2 ? 6 : 4}}
                className={clsx(
                  Styles.FeatureGrid__column,
                  (index === 1 || index === items.length - 1) && Styles['FeatureGrid__column--rowEnd'],
                )}
              >
                <div className={Styles.FeatureGrid__item}>
                  <FeatureGridVisual
                    visual={item.visual}
                    variant={isPlatform ? 'platform' : index < 2 ? 'feature' : 'compactFeature'}
                    productUI={{
                      shadowColor: SHADOW_COLORS[index] ?? 'blueDark',
                      shadowPosition: 'left-bottom',
                      roundedCorners: {topRight: false, bottomRight: false},
                    }}
                  />
                  <div className={Styles.FeatureGrid__body}>
                    <h3 id={`${titleId}-${index}`} className={Styles.FeatureGrid__title}>
                      {item.icon === 'mark-github' && <MarkGithubIcon size={24} aria-hidden="true" />}
                      <Text size={isPlatform ? '400' : '300'}>{item.title}</Text>
                    </h3>
                    <Text as="p" size="300" variant="muted">
                      {item.description}
                    </Text>
                    <div className={Styles.FeatureGrid__ctas}>
                      <Button
                        as="a"
                        href={item.cta1.url}
                        size="small"
                        variant={isPlatform ? 'primary' : 'secondary'}
                        {...getAnalyticsEvent({
                          action: item.cta1.label,
                          tag: 'a',
                          context: item.title,
                          location: 'features',
                        })}
                      >
                        {item.cta1.label}
                      </Button>
                      {item.cta2 && (
                        <Button
                          as="a"
                          href={item.cta2.url}
                          size="small"
                          variant="secondary"
                          {...getAnalyticsEvent({
                            action: item.cta2.label,
                            tag: 'a',
                            context: item.title,
                            location: 'features',
                          })}
                        >
                          {item.cta2.label}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Grid.Column>
            ))}
          </Grid>
        </div>
      </div>

      {hasColorDivider && <ColorDivider variant="2" />}
    </section>
  )
}
